import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Reorder } from "framer-motion";
import {
  X, Inbox, ListTodo, FileText, File, CalendarDays, BarChart3, Sparkles, Search,
  Settings as SettingsIcon, HeartPulse, Command, Plug, UserRound, Mail, Send,
  FileEdit, Archive, Trash2, MessageSquare, Folder as FolderIcon, Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AzureDevOpsLogo } from "@/components/BrandMarks";
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { SOURCE_REGISTRY } from "@/lib/sourceRegistry";
import type { RouteGlyph, RouteIcon } from "@/lib/pinnedViews";
import { usePinnedViews, type PinnedTab } from "@/hooks/use-pinned-views";

// The glyph half of the tab icons. Deliberately the same lucide icons the nav
// rail and Settings' own tab strip use for each concept (FolderNav.tsx's
// railSections/iconForProviderFolder, UnifiedSettings' TABS) — a pinned tab
// should look like the thing it points at, not like a second icon vocabulary.
const GLYPHS: Record<RouteGlyph, React.ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  tasks: ListTodo,
  docs: FileText,
  doc: File,
  calendar: CalendarDays,
  analytics: BarChart3,
  ai: Sparkles,
  search: Search,
  settings: SettingsIcon,
  preferences: SettingsIcon,
  wellbeing: HeartPulse,
  rules: Command,
  connections: Plug,
  account: UserRound,
  mail: Mail,
  sent: Send,
  drafts: FileEdit,
  archive: Archive,
  trash: Trash2,
  chat: MessageSquare,
  list: ListTodo,
  ado: AzureDevOpsLogo,
  folder: FolderIcon,
};

function TabIcon({ icon }: { icon: RouteIcon }) {
  if (icon.kind === "emoji") {
    // A list's own emoji, rendered at the same box size as an icon so tabs
    // stay aligned whether or not the list has one.
    return <span aria-hidden className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[11px] leading-none">{icon.emoji}</span>;
  }
  // Brand logos come from the source registry rather than a second mapping
  // here (INT-11), so a new provider picks up a tab icon for free.
  const Icon = icon.kind === "source" ? SOURCE_REGISTRY[icon.source].icon : GLYPHS[icon.glyph];
  return <Icon className="h-3.5 w-3.5 shrink-0" />;
}

// The pinned-view tab bar, along the bottom of the window from the left edge
// of the nav rail to the far right.
//
// It is absent entirely until there is at least one pin — no empty bar, no
// reserved space — which is why this renders null rather than an empty
// container: the app's own layout must be identical for a user who has never
// pinned anything.
//
// Mounted once in Layout, so it covers every screen rather than only the
// inbox. Its hooks run before the early return, which is also where the
// keyboard shortcuts live — Alt+P has to work on a screen with no pins yet,
// otherwise the only way to make the bar appear is the mouse.
export function PinnedTabsBar() {
  const [location, setLocation] = useLocation();
  const { tabs, currentPath, isCurrentPinned, canPinCurrent, togglePinCurrent, closeTab, renameTab, reorderTabs } = usePinnedViews();

  // Local mirror of the server order so a drag animates immediately instead
  // of waiting for a round trip — resynced whenever the query settles. Same
  // pattern as FolderGroup's own reorder.
  const [order, setOrder] = useState<PinnedTab[]>(tabs);
  useEffect(() => { setOrder(tabs); }, [tabs]);

  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  // A completed drag ends with a click event on the tab, which would
  // otherwise navigate away from the screen the user was reordering from.
  const draggedRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Alt rather than Cmd/Ctrl: Cmd+P is print, Cmd+D is bookmark, and
      // Cmd+1..9 is the browser's own tab switching — all of which a web app
      // either can't have or shouldn't take.
      if (!event.altKey || event.ctrlKey || event.metaKey) return;

      // Ignore anything typed into an input, textarea or the rich-text
      // editor — Alt+digit is a real character on a Mac keyboard.
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable='true']")) return;

      if (event.code === "KeyP") {
        event.preventDefault();
        if (canPinCurrent || isCurrentPinned) togglePinCurrent();
        return;
      }

      const digit = /^Digit([1-9])$/.exec(event.code);
      if (digit) {
        const tab = order[Number(digit[1]) - 1];
        if (!tab) return;
        event.preventDefault();
        setLocation(tab.path);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [order, canPinCurrent, isCurrentPinned, togglePinCurrent, setLocation]);

  if (order.length === 0) return null;

  const commitRename = (tab: PinnedTab) => {
    const trimmed = renameDraft.trim();
    setRenamingId(null);
    // Clearing the name is how you undo a rename: an empty value hands the
    // tab back to auto-naming rather than being rejected.
    if (!trimmed) {
      if (tab.isCustomLabel) renameTab(tab.id, null);
      return;
    }
    if (trimmed !== tab.displayLabel) renameTab(tab.id, trimmed);
  };

  return (
    <nav
      aria-label="Pinned views"
      data-testid="pinned-tabs-bar"
      className="z-20 flex h-10 shrink-0 items-stretch gap-1 overflow-x-auto border-t border-border bg-card/30 px-2 py-1"
    >
      <Reorder.Group
        as="div"
        axis="x"
        values={order}
        onReorder={setOrder}
        className="flex items-stretch gap-1"
      >
        {order.map((tab, index) => {
          const active = tab.path === currentPath || tab.path === location;
          const renaming = renamingId === tab.id;
          return (
            <Reorder.Item
              key={tab.id}
              value={tab}
              as="div"
              onDragStart={() => { draggedRef.current = true; }}
              onDragEnd={() => {
                reorderTabs(order.map((t) => t.id));
                // Cleared after the click that ends the drag has been and
                // gone, not synchronously — that click is the one to swallow.
                setTimeout(() => { draggedRef.current = false; }, 0);
              }}
              className={cn(
                "group flex min-w-0 shrink-0 items-center gap-1 rounded-md border pl-2 pr-1 text-sm transition-colors",
                active
                  ? "border-primary/50 bg-background text-foreground"
                  : "border-border bg-transparent text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {renaming ? (
                <input
                  autoFocus
                  value={renameDraft}
                  aria-label={`Rename ${tab.displayLabel} tab`}
                  onChange={(event) => setRenameDraft(event.target.value)}
                  onBlur={() => commitRename(tab)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") { event.preventDefault(); commitRename(tab); }
                    if (event.key === "Escape") { event.preventDefault(); setRenamingId(null); }
                  }}
                  className="w-28 bg-transparent text-sm text-foreground outline-none"
                />
              ) : (
                // Right-click is the discoverable way to rename — double-click
                // still works, but nothing on a tab advertises it, and a
                // rename control crammed alongside the close button would
                // leave no room for the name itself.
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <button
                      type="button"
                      onClick={() => {
                        if (draggedRef.current) return;
                        setLocation(tab.path);
                      }}
                      onDoubleClick={() => { setRenamingId(tab.id); setRenameDraft(tab.displayLabel); }}
                      aria-current={active ? "page" : undefined}
                      // The target is worth showing on hover: two tabs can
                      // share a name (two searches, two lists), and a tab
                      // whose target is gone otherwise gives no hint why it
                      // looks stale.
                      title={`${tab.displayLabel} — ${tab.path}${tab.isResolvable ? "" : " (no longer available)"}\nRight-click to rename${index < 9 ? ` · Alt+${index + 1}` : ""}`}
                      className={cn(
                        "flex min-w-0 items-center gap-1.5 py-1 text-left outline-none focus-visible:underline",
                        !tab.isResolvable && "italic opacity-60",
                      )}
                    >
                      <TabIcon icon={tab.icon} />
                      <span className="max-w-[11rem] truncate">{tab.displayLabel}</span>
                    </button>
                  </ContextMenuTrigger>
                  <ContextMenuContent className="w-44">
                    <ContextMenuItem onSelect={() => { setRenamingId(tab.id); setRenameDraft(tab.displayLabel); }}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Rename
                    </ContextMenuItem>
                    {tab.isCustomLabel && (
                      // Only offered once there's a custom name to drop —
                      // otherwise it reads as a second, mysterious rename.
                      <ContextMenuItem onSelect={() => renameTab(tab.id, null)}>
                        <Sparkles className="mr-2 h-3.5 w-3.5" />
                        Reset to automatic name
                      </ContextMenuItem>
                    )}
                    <ContextMenuItem onSelect={() => closeTab(tab.id)}>
                      <X className="mr-2 h-3.5 w-3.5" />
                      Close tab
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              )}

              <button
                type="button"
                onClick={() => closeTab(tab.id)}
                aria-label={`Close ${tab.displayLabel} tab`}
                title={`Close ${tab.displayLabel}`}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
    </nav>
  );
}
